export const ALERT_PROVIDERS = ['gsc', 'yandex', 'other'] as const;
export type AlertProvider = (typeof ALERT_PROVIDERS)[number];
export const ALERT_PRIORITIES = ['critical', 'warning', 'info'] as const;
export type AlertPriority = (typeof ALERT_PRIORITIES)[number];
export const YANDEX_SEVERITIES = [
  'FATAL',
  'CRITICAL',
  'POSSIBLE_PROBLEM',
  'RECOMMENDATION',
] as const;
export interface YandexAlertSettings {
  enabled: boolean;
  source: 'api' | 'mail';
  senders: string[];
  propertyIds: string[];
  severities: (typeof YANDEX_SEVERITIES)[number][];
  intervalMinutes: number;
  notifyResolved: boolean;
}
export interface AlertRule {
  id: string;
  name: string;
  provider: AlertProvider;
  subjectIncludes: string[];
  priority: AlertPriority;
  notify: boolean;
}
export interface AlertSettings {
  revision: number;
  enabled: boolean;
  channelId: string | null;
  topicId: number | null;
  senders: string[];
  sites: string[];
  /** Legacy rules are retained only in historical snapshots; never used for new mail. */
  rules?: AlertRule[];
  otherSenders: string[];
  ai: { model: string; prompt: string; notifyPriorities: AlertPriority[] };
  yandex: YandexAlertSettings;
}
export interface AlertMail {
  mailbox: string;
  messageId: string;
  sender: string;
  subject: string;
  text: string;
  receivedAt: string;
}
export const ALERT_AI_CATEGORIES = [
  'DMCA / Legal',
  'Downtime / Server',
  'SEO / Indexing',
  'Security / Malware',
  'Billing / Domains',
  'System / Trash',
] as const;
export const ALERT_AI_PRIORITIES = ['Критический', 'Средний', 'Низкий'] as const;
export const ALERT_PRIORITY_LABELS: Record<AlertPriority, string> = {
  critical: 'Критический',
  warning: 'Средний',
  info: 'Низкий',
};
export interface AlertAiResult {
  category: (typeof ALERT_AI_CATEGORIES)[number] | null;
  priority: (typeof ALERT_AI_PRIORITIES)[number] | null;
  summary: string;
  action_required: boolean | null;
}
export interface AlertAiAnalysis {
  model: string;
  responseId: string | null;
  result: AlertAiResult | null;
  error: string | null;
  usage: { inputTokens: number; outputTokens: number } | null;
}
export interface AlertAiInfo {
  configured: boolean;
  /** Server default, used until a workspace saves its own selection. */
  model: string;
  models: Array<{ id: string; label: string; description: string }>;
}
export interface AlertDecision {
  provider: AlertProvider | null;
  site: string | null;
  ruleId: string | null;
  priority: AlertPriority | null;
  action: 'notify' | 'ignore' | 'review';
  reason: string;
  analysis?: AlertAiAnalysis;
}
export interface AlertView extends AlertDecision {
  source: 'api' | 'mail';
  id: string;
  subject: string;
  sender: string;
  receivedAt: string;
  createdAt: string;
  settingsRevision: number;
  status: string;
  error: string | null;
  sentAt: string | null;
}
export interface AlertPage {
  items: AlertView[];
  total: number;
  page: number;
}
export interface AlertDetail extends AlertView {
  evidence: AlertMail | null;
  settings: AlertSettings;
  messageId: number | null;
  chatId: string | null;
  topicId: number | null;
}
export interface YandexDiagnostic {
  severity: string;
  state: string;
  last_state_update: string | null;
}
export interface AlertSyncView {
  propertyId: string;
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  error: string | null;
}
